import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { parseAddressFeatureComplete, type AddressFeature } from '../lib/addressUtils';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string, city: string, zip: string, lat: number | null, lng: number | null) => void;
  placeholder?: string;
  className?: string;
}

type AddressResult = AddressFeature;

export default function AddressAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Rechercher une adresse...",
  className = ""
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<AddressResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setSuggestions(data.features);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue, '', '', null, null);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (newValue.length >= 3) {
        searchAddress(newValue);
      }
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion: AddressResult) => {
    try {
      const parsed = await parseAddressFeatureComplete(suggestion);
      onChange(parsed.address, parsed.city, parsed.zip, parsed.latitude, parsed.longitude);
      setShowSuggestions(false);
      setSuggestions([]);
    } catch (error) {
      console.error('Erreur lors de la sélection de l\'adresse:', error);
      const props = suggestion.properties;
      const address = props.name || props.label || '';
      onChange(address, props.city || '', props.postcode || '', null, null);
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  const formatSuggestion = (suggestion: AddressResult): string => {
    const props = suggestion.properties;
    return props.label || props.name;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && suggestions.length > 0 && showSuggestions) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[0]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        className={className}
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls="address-suggestions"
        role="combobox"
        aria-label="Recherche d'adresse"
      />
      
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div 
          id="address-suggestions"
          role="listbox"
          aria-label="Suggestions d'adresses"
          className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-300 rounded-md shadow-xl max-h-60 overflow-auto"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 text-xs font-semibold text-blue-700">
            Cliquez sur une adresse ou appuyez sur Entrée pour sélectionner la première
          </div>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectSuggestion(suggestion);
              }}
              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900">
                {formatSuggestion(suggestion)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

